import { z } from 'zod';

/**
 * Base schema for objective (without refinements)
 */
const ObjectiveBaseSchema = z.object({
  title: z.string().min(3, 'O título deve ter no mínimo 3 caracteres').max(100, 'O título deve ter no máximo 100 caracteres'),
  description: z.string().max(500, 'A descrição deve ter no máximo 500 caracteres').optional(),
  level: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)], {
    errorMap: () => ({ message: 'O nível deve ser 1, 2, 3 ou 4' })
  }),
  type: z.enum(['committed', 'aspirational']).default('committed'),
  status: z.enum(['draft', 'active', 'completed', 'rolled', 'cancelled']).default('draft'),
  cycleId: z.string().min(1, 'O ID do ciclo é obrigatório'),
  ownerId: z.string().min(1, 'O ID do proprietário é obrigatório'),
  ownerName: z.string().min(1, 'O nome do proprietário é obrigatório'),
  ownerEmail: z.string().email('Formato de e-mail inválido'),
  parentObjectiveId: z.string().nullable().optional(),
  parentObjectivePath: z.string().nullable().optional(),
  path: z.string().min(1, 'O caminho é obrigatório'),
  ancestorIds: z.array(z.string()).default([]),
  startDate: z.any(), // Accepts Date or Timestamp
  endDate: z.any(), // Accepts Date or Timestamp
  allowedRoles: z.array(z.string()).min(1, 'Pelo menos uma função deve ser permitida').default(['admin'])
});

/**
 * Schema Zod para criar um novo objetivo
 */
export const ObjectiveCreateSchema = ObjectiveBaseSchema.refine(
  (data) => {
    // Se parentObjectiveId for fornecido, parentObjectivePath também deve ser fornecido
    if (data.parentObjectiveId && !data.parentObjectivePath) {
      return false;
    }
    return true;
  },
  {
    message: 'O caminho do objetivo pai deve ser fornecido quando o ID do objetivo pai estiver definido',
    path: ['parentObjectivePath']
  }
);

export type ObjectiveCreate = z.infer<typeof ObjectiveCreateSchema>;

/**
 * Schema Zod para atualizar um objetivo existente
 */
export const ObjectiveUpdateSchema = ObjectiveBaseSchema.partial().extend({
  id: z.string().min(1, 'O ID do objetivo é obrigatório'),
  progress: z.number().min(0, 'O progresso deve ser no mínimo 0').max(100, 'O progresso deve ser no máximo 100').optional(),
  keyResultCount: z.number().min(0, 'A contagem de resultados-chave não pode ser negativa').optional(),
  completedAt: z.instanceof(Date).nullable().optional()
}).refine(
  (data) => {
    // Valida transições de status
    if (data.status) {
      const invalidTransitions = ['completed', 'rolled', 'cancelled'];
      // Esta é uma verificação simplificada - a validação completa deve ser feita na camada de serviço
      return true;
    }
    return true;
  },
  {
    message: 'Transição de status inválida',
    path: ['status']
  }
);

export type ObjectiveUpdate = z.infer<typeof ObjectiveUpdateSchema>;

/**
 * Schema Zod completo para entidade de objetivo com todos os campos
 */
export const ObjectiveSchema = ObjectiveBaseSchema.extend({
  id: z.string().min(1, 'O ID do objetivo é obrigatório'),
  progress: z.number().min(0).max(100).default(0),
  keyResultCount: z.number().min(0).default(0),
  createdAt: z.instanceof(Date),
  updatedAt: z.instanceof(Date),
  completedAt: z.instanceof(Date).nullable().optional(),
  _metadata: z.object({
    version: z.number().int().positive().default(1),
    lastModifiedBy: z.string().min(1, 'O campo de última modificação por é obrigatório')
  })
});

export type Objective = z.infer<typeof ObjectiveSchema>;

/**
 * Form-specific validation schema for Objective forms
 * Used with React Hook Form for client-side validation
 * Includes Portuguese error messages for better UX
 */
export const ObjectiveFormSchema = z.object({
  title: z
    .string()
    .min(3, 'Título deve ter pelo menos 3 caracteres')
    .max(200, 'Título não pode ter mais de 200 caracteres')
    .trim(),

  description: z
    .string()
    .max(1000, 'Descrição não pode ter mais de 1000 caracteres')
    .trim()
    .optional()
    .or(z.literal('')),

  cycleId: z
    .string({
      required_error: 'Ciclo é obrigatório',
    })
    .min(1, 'Ciclo é obrigatório'),

  ownerId: z
    .string({
      required_error: 'Responsável é obrigatório',
    })
    .min(1, 'Responsável é obrigatório'),

  level: z
    .number({
      required_error: 'Nível é obrigatório',
      invalid_type_error: 'Nível inválido',
    })
    .min(1)
    .max(4),

  startDate: z.date({
    required_error: 'Data de início é obrigatória',
    invalid_type_error: 'Data de início inválida',
  }),

  endDate: z.date({
    required_error: 'Data de término é obrigatória',
    invalid_type_error: 'Data de término inválida',
  }),

  parentId: z.string().optional().or(z.literal('')),
});

/**
 * TypeScript type inferred from ObjectiveFormSchema
 */
export type ObjectiveFormData = z.infer<typeof ObjectiveFormSchema>;

/**
 * Default values for Objective form initialization
 */
export const objectiveFormDefaults: Partial<ObjectiveFormData> = {
  title: '',
  description: '',
  level: 1,
  parentId: '',
  startDate: undefined,
  endDate: undefined,
};
